import React, { useState, useEffect, useRef } from 'react'
import useMobile from '../../../../hooks/useMobile'
import ReactApexChart from 'react-apexcharts'
import '../../home-style.scss'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import {
  fetchGraphV2aggr
} from '../../../../services/api'
import dayjs from 'dayjs'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Box,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { saveAs } from 'file-saver'
import usePotfolioRepGraphStyles from './styles'

const PotfolioRepGraph = ({ token }) => {
  const classes = usePotfolioRepGraphStyles()
  const isMobile = useMobile()

  const [technicalFromDate, setTechnicalFromDate] = React.useState(
    dayjs(new Date()).subtract(11, 'month')
  )
  const [technicalToDate, setTechnicalToDate] = React.useState(
    dayjs(new Date())
  )
  const [plantcountDuration, setPlantcountGraphDuration] = useState('year')
  const [mapPlantSizeSeries, setMapPlantSizeSeries] = React.useState([])
  const scrollRef = useRef(null)

  // Scroll to right on mobile when data loads
  useEffect(() => {
    if (isMobile && scrollRef.current && mapPlantSizeSeries.length > 0 && mapPlantSizeSeries[0].data?.length > 0) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
        }
      }, 500)
    }
  }, [isMobile, mapPlantSizeSeries])
  const [graphTotalType] = useState('etoday')
  const [open, setOpen] = useState(false)
  const [openTable, setOpenTable] = useState(false)
  // Remove datepickerDisable state - dates should always be selectable

  // Add state to track if user has manually selected dates
  const [userSelectedDates, setUserSelectedDates] = useState(false)

  const [areaplantData] = useState({
    options: {
      chart: {
        height: 350,
        type: 'line',
        zoom: {
          enabled: false,
        },
        toolbar: {
          export: {
            csv: {
              filename: 'PlantSize',
              columnDelimiter: ',',
              headerCategory: 'Date',
              headerValue: 'value',
              dateFormatter(timestamp) {
                return new Date(timestamp).toDateString()
              },
            },
            svg: {
              filename: 'PlantSize',
            },
            png: {
              filename: 'PlantSize',
            },
          },
        },
      },
      colors: ['#6CB759', '#5968B7'], // Swapped colors - Green for Plant Count, Blue for Plant Size
      stroke: {
        width: [4, 0], // Swapped stroke widths - Line for Plant Count, Column for Plant Size
        curve: 'smooth',
      },
      title: {},
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0], // Enable data labels for Plant Count (the line)
        formatter: function (val) {
          return parseFloat(val).toFixed(2)
        },
      },
      yaxis: [
        {
          title: {
            text: 'Plant Count (Nos.)',
            style: { color: '#5968B7', fontSize: '12px' },
          },
          labels: {
            formatter: function (val) {
              return parseFloat(val).toFixed(2)
            },
          },
        },
        {
          opposite: true,
          title: {
            text: 'Plant Capacity (MW)',
            style: { color: '#5968B7', fontSize: '12px' },
          },
          labels: {
            formatter: function (val) {
              return parseFloat(val).toFixed(2)
            },
          },
        },
      ],
      xaxis: {
        type: 'category',
        floating: false,
        labels: {
          show: true,
          rotate: -45,
          rotateAlways: true,
          hideOverlappingLabels: true,
          showDuplicates: false,
          trim: false,
          minHeight: undefined,
          maxHeight: 120,
          style: {
            colors: [],
            fontSize: '12px',
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontWeight: 400,
            cssClass: 'apexcharts-xaxis-label',
          },
          offsetX: 0,
          offsetY: 0,
          format: 'MMMM',
          formatter: function (value, timestamp, opts) {
            return dayjs(value).format('MMM-YY')
          },
          datetimeUTC: false,
          datetimeFormatter: {
            year: 'yyyy',
            month: "MMM 'yy",
            day: 'dd MMM',
            hour: 'HH:mm',
          },
        },
      },
      tooltip: {
        enabled: true,
        x: {
          show: true,
          format: 'MMMM',
          formatter: function (value, opts) {
            return dayjs(
              opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex].x
            ).format('MMMM-YYYY')
          },
        },
        y: {
          formatter: function (val) {
            return parseFloat(val).toFixed(2)
          },
        },
      },
      grid: {
        yaxis: {
          lines: {
            offsetX: -30,
          },
        },
        padding: {
          left: 20,
        },
      },
      legend: {
        show: true,
        position: 'bottom',
      },
    },
  })



  useEffect(() => {
    handleplantcountGraph('plant', 'year')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Only trigger graph update if user has manually selected dates or duration is custom
    if (userSelectedDates || plantcountDuration === 'custom') {
      handleplantcountGraph('plant', plantcountDuration)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicalFromDate, technicalToDate, userSelectedDates])

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleChooseDate = (value, type) => {
    // Mark that user has manually selected dates
    setUserSelectedDates(true)

    if (type === 'from') {
      setTechnicalFromDate(value)
      // Validate that from date is not after to date
      if (value.isAfter(technicalToDate)) {
        setTechnicalToDate(value.add(1, 'day'))
      }
    } else if (type === 'to') {
      setTechnicalToDate(value)
      // Validate that to date is not before from date
      if (value.isBefore(technicalFromDate)) {
        setTechnicalFromDate(value.subtract(1, 'day'))
      }
    }
  }

  const handleselectButton = (ButtonType) => {
    // Reset user selected dates flag when using predefined buttons
    setUserSelectedDates(false)

    switch (ButtonType) {
      case 'year':
        const previous1yData = dayjs(dayjs(new Date())).subtract(11, 'month')
        setTechnicalFromDate(previous1yData)
        setTechnicalToDate(dayjs(new Date()))
        handleplantcountGraph(graphTotalType, 'year')
        break
      case '1m':
        const previous1Data = dayjs(dayjs(new Date())).subtract(29, 'day')
        setTechnicalFromDate(previous1Data)
        setTechnicalToDate(dayjs(new Date()))
        handleplantcountGraph(graphTotalType, '1m')
        break
      case '3m':
        const previous3Data = dayjs(dayjs(new Date())).subtract(2, 'month')
        setTechnicalFromDate(previous3Data)
        setTechnicalToDate(dayjs(new Date()))
        handleplantcountGraph(graphTotalType, '3m')
        break
      case '6m':
        const previous6Data = dayjs(dayjs(new Date())).subtract(5, 'month')
        setTechnicalFromDate(previous6Data)
        setTechnicalToDate(dayjs(new Date()))
        handleplantcountGraph(graphTotalType, '6m')
        break
      case 'mtd':
        const firstDateOfMonth = dayjs(new Date()).startOf('month')
        setTechnicalFromDate(firstDateOfMonth)
        setTechnicalToDate(dayjs(new Date()))
        handleplantcountGraph(graphTotalType, 'mtd')
        break
      case 'ytd':
        const firstDateOfYear = dayjs(new Date()).startOf('year')
        setTechnicalFromDate(firstDateOfYear)
        setTechnicalToDate(dayjs(new Date()))
        handleplantcountGraph(graphTotalType, 'ytd')
        break
      case 'custom':
        // For custom, we use the currently selected dates
        handleplantcountGraph(graphTotalType, 'custom')
        break
      default:
        break
    }
  }

  const handleplantcountGraph = (type, duration) => {
    setPlantcountGraphDuration(duration)



    // Reset the mapPlantSizeSeries array to ensure correct order
    setMapPlantSizeSeries([])

    // API calls with custom sort type and selected date range
    // First, fetch and process Plant Count data (green line)
    fetchGraphV2aggr(
      'plantcount',
      dayjs().format('YYYY-MM-DD'),
      duration,
      token,
      'all'
    ).then((resp) => {
      if (resp) {
        const newCountData = resp.map((obj) => ({
          x: obj.x,
          y: obj.y,
        }))

        setMapPlantSizeSeries((prevSeries) => {
          const newSeries = [...prevSeries]
          const countSeriesIndex = newSeries.findIndex(
            (series) => series.name === 'Plant-Count'
          )

          const countSeries = {
            name: 'Plant-Count',
            type: 'line',
            data: newCountData,
            yaxisIndex: 0,
          }

          if (countSeriesIndex >= 0) {
            newSeries[countSeriesIndex] = countSeries
          } else {
            newSeries.unshift(countSeries)
          }

          return newSeries
        })
      }
    })

    // Then, fetch and process Plant Size data (blue columns)
    fetchGraphV2aggr(
      'plantsize',
      dayjs().format('YYYY-MM-DD'),
      duration,
      token,
      'all',
    ).then((resp) => {
      if (resp) {
        const newSizeData = resp.map((obj) => ({
          x: obj.x,
          y: obj.y
        }))

        setMapPlantSizeSeries((prevSeries) => {
          const newSeries = [...prevSeries]
          const sizeSeriesIndex = newSeries.findIndex(
            (series) => series.name === 'Plant-Size'
          )

          const sizeSeries = {
            name: 'Plant-Size',
            type: 'column',
            data: newSizeData,
            yaxisIndex: 1,
          }

          if (sizeSeriesIndex >= 0) {
            newSeries[sizeSeriesIndex] = sizeSeries
          } else {
            newSeries.push(sizeSeries)
          }

          return newSeries
        })
      }
    })
  }

  const handlePlantcountgraphSort = (type) => {
    // Always allow date selection - remove the disable logic
    handleselectButton(type)
  }

  const exportToCSV = (rows) => {
    const headings = ['Date', ...rows.map((item) => item.name)]
    const csvContent = [headings.join(',')]
    const allDates = new Set()
    rows.forEach((item) =>
      item.data.forEach((row) =>
        allDates.add(dayjs(row.x).format('YYYY/MM/DD'))
      )
    )
    allDates.forEach((date) => {
      const rowData = [date]
      rows.forEach((item) => {
        const dataForDate = item.data.find(
          (row) => dayjs(row.x).format('YYYY/MM/DD') === date
        )
        rowData.push(dataForDate ? dataForDate.y : 0)
      })
      csvContent.push(rowData.join(','))
    })
    const blob = new Blob([csvContent.join('\n')], {
      type: 'text/csv;charset=utf-8',
    })
    saveAs(blob, 'PlantSize.csv')
  }

  const datePickerStyle = {
    width: '130px',
  }
  const dynamicHeadings = mapPlantSizeSeries.map((item) => item.name)

  return (
    <div className={classes.root}>
      <div
        className="graph_sm_bg"
        style={{ minHeight: '510px', backgroundColor: 'white' }}
      >
        {isMobile ? (
          <Stack spacing={1}>
            {/* Row 1: Toggle and Fullscreen */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: '100%', px: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <div className="check_graph">
                  <input
                    id="PotfolioRepGraphMobile"
                    type="checkbox"
                    checked={openTable}
                    onChange={() => setOpenTable(!openTable)}
                  />
                  <label htmlFor="PotfolioRepGraphMobile"></label>
                </div>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {openTable && (
                  <IconButton
                    size="small"
                    onClick={() => exportToCSV(mapPlantSizeSeries)}
                    sx={{ color: 'primary.main' }}
                  >
                    <VerticalAlignBottomIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={handleOpen}
                  sx={{ color: 'primary.main' }}
                >
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>

            {/* Row 2: Date Pickers */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ px: 1, py: 0.5 }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <DatePicker
                    label="From Date"
                    minDate={dayjs(new Date()).subtract(2, 'year')}
                    maxDate={technicalToDate}
                    format="DD-MM-YYYY"
                    value={technicalFromDate}
                    onChange={(value) => handleChooseDate(value, 'from')}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true
                      }
                    }}
                  />
                </Box>
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <DatePicker
                    label="To Date"
                    maxDate={dayjs()}
                    minDate={technicalFromDate}
                    format="DD-MM-YYYY"
                    value={technicalToDate}
                    onChange={(value) => handleChooseDate(value, 'to')}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true
                      }
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Stack>

            {/* Row 4: Duration Filters */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                px: 1,
                pb: 1,
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {[
                { label: 'CUST', value: 'custom' },
                { label: 'YTD', value: 'ytd' },
                { label: 'MTD', value: 'mtd' },
                { label: '1 Y', value: 'year' },
                { label: '6 M', value: '6m' },
                { label: '3 M', value: '3m' },
                { label: '1 M', value: '1m' },
              ].map((filter) => (
                <div
                  key={filter.value}
                  className={
                    plantcountDuration === filter.value ? 'day_s_bottomline' : 'day_s_b'
                  }
                  onClick={() => handlePlantcountgraphSort(filter.value)}
                  style={{ flexShrink: 0, cursor: 'pointer' }}
                >
                  {filter.label}
                </div>
              ))}
            </Stack>

            {/* Graph Content */}
            {openTable ? (
              <div style={{ maxHeight: '360px', overflow: 'auto' }}>
                <TableContainer component={Paper}>
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        {dynamicHeadings?.map((heading, index) => (
                          <TableCell
                            key={index}
                            style={{ textTransform: 'capitalize' }}
                          >
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mapPlantSizeSeries?.[0]?.data.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          <TableCell>{dayjs(row.x).format('YYYY/MM/DD')}</TableCell>
                          {mapPlantSizeSeries?.map((item, index) => (
                            <TableCell key={index}>
                              {item.data.find((entry) => entry.x === row.x)?.y || 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            ) : (
              <div
                style={{
                  overflowX: 'auto',
                  width: '100%',
                  WebkitOverflowScrolling: 'touch',
                  overflowY: 'hidden'
                }}
              >
                <div style={{ width: '800px' }}>
                  <div id="chart">
                    <ReactApexChart
                      options={areaplantData.options}
                      series={mapPlantSizeSeries}
                      legend={areaplantData.legend}
                      height={350}
                    />
                  </div>
                </div>
              </div>
            )}
          </Stack>
        ) : (
          <>
            <div className="align_end mb_10">
              <div className="check_graph mr_8">
                <input
                  id="PotfolioRepGraph"
                  type="checkbox"
                  checked={openTable}
                  onChange={() => setOpenTable(!openTable)}
                />
                <label htmlFor="PotfolioRepGraph"></label>
              </div>
              <div className="mr_8">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <div style={datePickerStyle}>
                      <DatePicker
                        style={{ width: '120px !important' }}
                        label="From Date"
                        minDate={dayjs(new Date()).subtract(2, 'year')}
                        maxDate={technicalToDate}
                        className={classes.datePicker}
                        format="DD-MM-YYYY"
                        value={technicalFromDate}
                        onChange={(value) => handleChooseDate(value, 'from')}
                      // Remove disabled prop - always allow date selection
                      />
                    </div>
                  </DemoContainer>
                </LocalizationProvider>
              </div>
              <div className="mr_8">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <div style={datePickerStyle}>
                      <DatePicker
                        style={{ width: '120px !important' }}
                        label="To Date"
                        maxDate={dayjs()}
                        minDate={technicalFromDate}
                        className={classes.datePicker}
                        format="DD-MM-YYYY"
                        value={technicalToDate}
                        onChange={(value) => handleChooseDate(value, 'to')}
                      // Remove disabled prop - always allow date selection
                      />
                    </div>
                  </DemoContainer>
                </LocalizationProvider>
              </div>
              {openTable && (
                <div className="mr_8">
                  <VerticalAlignBottomIcon
                    className="i_clr pointer"
                    onClick={() => exportToCSV(mapPlantSizeSeries)}
                  />
                </div>
              )}
              <div className="mt8">
                <FullscreenIcon className="i_clr pointer" onClick={handleOpen} />
              </div>
            </div>
            <div className="align_btw mb_10">
              <div className="h2"></div>
              <div>
                <div
                  className={
                    plantcountDuration === 'custom'
                      ? 'day_s_bottomline'
                      : 'day_s_b '
                  }
                  onClick={() => handlePlantcountgraphSort('custom')}
                >
                  CUST
                </div>
                <div
                  className={
                    plantcountDuration === 'ytd' ? 'day_s_bottomline' : 'day_s_b '
                  }
                  onClick={() => handlePlantcountgraphSort('ytd')}
                >
                  YTD
                </div>
                <div
                  className={
                    plantcountDuration === 'mtd' ? 'day_s_bottomline' : 'day_s_b '
                  }
                  onClick={() => handlePlantcountgraphSort('mtd')}
                >
                  MTD
                </div>
                <div
                  className={
                    plantcountDuration === 'year' ? 'day_s_bottomline' : 'day_s_b '
                  }
                  onClick={() => handlePlantcountgraphSort('year')}
                >
                  1 Y
                </div>
                <div
                  className={
                    plantcountDuration === '6m' ? 'day_s_bottomline' : 'day_s_b '
                  }
                  onClick={() => handlePlantcountgraphSort('6m')}
                >
                  6 M
                </div>
                <div
                  className={
                    plantcountDuration === '3m' ? 'day_s_bottomline' : 'day_s_b '
                  }
                  onClick={() => handlePlantcountgraphSort('3m')}
                >
                  3 M
                </div>
                <div
                  className={
                    plantcountDuration === '1m' ? 'day_s_bottomline' : 'day_s_b '
                  }
                  onClick={() => handlePlantcountgraphSort('1m')}
                >
                  1 M
                </div>
              </div>
            </div>
            {openTable ? (
              <div style={{ maxHeight: '360px', overflow: 'auto' }}>
                <TableContainer component={Paper}>
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        {dynamicHeadings?.map((heading, index) => (
                          <TableCell
                            key={index}
                            style={{ textTransform: 'capitalize' }}
                          >
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mapPlantSizeSeries?.[0]?.data.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          <TableCell>{dayjs(row.x).format('YYYY/MM/DD')}</TableCell>
                          {mapPlantSizeSeries?.map((item, index) => (
                            <TableCell key={index}>
                              {item.data.find((entry) => entry.x === row.x)?.y || 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            ) : (
              <div>
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Portfolio View (MW and Count)
                  </div>
                </div>
                <div id="chart">
                  <ReactApexChart
                    key={`portfolio-chart-${mapPlantSizeSeries.map(s => s.name).join('-')}`}
                    options={areaplantData.options}
                    series={mapPlantSizeSeries}
                    legend={areaplantData.legend}
                    height={350}
                  />
                </div>
              </div>
            )}
          </>
        )}

        <Dialog fullScreen open={open} onClose={handleClose}>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              zIndex: 1000
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogTitle></DialogTitle>
          <DialogContent>
            <DialogContentText>
              <div className="graph_bg">
                <div className="align_btw mb_10">
                  <div>
                    <div className="h1">Portfolio Representation</div>
                  </div>
                  <div className={classes.root}>
                    <div className="align_end">
                      <div className="check_graph mr_8">
                        <input
                          id="PotfolioRepGraphFull"
                          type="checkbox"
                          checked={openTable}
                          onChange={() => setOpenTable(!openTable)}
                        />
                        <label htmlFor="PotfolioRepGraphFull"></label>
                      </div>
                      <div className="mr_8">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={['DatePicker']}>
                            <div>
                              <DatePicker
                                label="From Date"
                                minDate={dayjs(new Date()).subtract(2, 'year')}
                                maxDate={technicalToDate}
                                format="DD-MM-YYYY"
                                value={technicalFromDate}
                                onChange={(value) => handleChooseDate(value, 'from')}
                              />
                            </div>
                          </DemoContainer>
                        </LocalizationProvider>
                      </div>
                      <div className="mr_8">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={['DatePicker']}>
                            <div>
                              <DatePicker
                                label="To Date"
                                maxDate={dayjs()}
                                minDate={technicalFromDate}
                                format="DD-MM-YYYY"
                                value={technicalToDate}
                                onChange={(value) => handleChooseDate(value, 'to')}
                              />
                            </div>
                          </DemoContainer>
                        </LocalizationProvider>
                      </div>
                      {openTable && (
                        <div className="">
                          <VerticalAlignBottomIcon
                            className="i_clr pointer"
                            onClick={() => exportToCSV(mapPlantSizeSeries)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="align_end mb_10">
                  <div>
                    <div
                      className={
                        plantcountDuration === 'custom'
                          ? 'day_s_bottomline'
                          : 'day_s_b '
                      }
                      onClick={() => handlePlantcountgraphSort('custom')}
                    >
                      CUST
                    </div>
                    <div
                      className={
                        plantcountDuration === 'ytd'
                          ? 'day_s_bottomline'
                          : 'day_s_b '
                      }
                      onClick={() => handlePlantcountgraphSort('ytd')}
                    >
                      YTD
                    </div>
                    <div
                      className={
                        plantcountDuration === 'mtd'
                          ? 'day_s_bottomline'
                          : 'day_s_b '
                      }
                      onClick={() => handlePlantcountgraphSort('mtd')}
                    >
                      MTD
                    </div>
                    <div
                      className={
                        plantcountDuration === 'year'
                          ? 'day_s_bottomline'
                          : 'day_s_b '
                      }
                      onClick={() => handlePlantcountgraphSort('year')}
                    >
                      1 Y
                    </div>
                    <div
                      className={
                        plantcountDuration === '6m'
                          ? 'day_s_bottomline'
                          : 'day_s_b '
                      }
                      onClick={() => handlePlantcountgraphSort('6m')}
                    >
                      6 M
                    </div>
                    <div
                      className={
                        plantcountDuration === '3m'
                          ? 'day_s_bottomline'
                          : 'day_s_b '
                      }
                      onClick={() => handlePlantcountgraphSort('3m')}
                    >
                      3 M
                    </div>
                    <div
                      className={
                        plantcountDuration === '1m'
                          ? 'day_s_bottomline'
                          : 'day_s_b '
                      }
                      onClick={() => handlePlantcountgraphSort('1m')}
                    >
                      1 M
                    </div>
                  </div>
                </div>
                {openTable ? (
                  <div>
                    <TableContainer component={Paper}>
                      <Table aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            {dynamicHeadings?.map((heading, index) => (
                              <TableCell
                                key={index}
                                style={{ textTransform: 'capitalize' }}
                              >
                                {heading}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {mapPlantSizeSeries?.[0]?.data?.map(
                            (row, rowIndex) => (
                              <TableRow key={rowIndex}>
                                <TableCell>
                                  {dayjs(row.x).format('YYYY/MM/DD')}
                                </TableCell>
                                {mapPlantSizeSeries?.map((item, index) => (
                                  <TableCell key={index}>
                                    {item.data.find(
                                      (entry) => entry.x === row.x
                                    )?.y || 0}
                                  </TableCell>
                                ))}
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                ) : (
                  <div>
                    <div id="chart" ref={scrollRef} style={{ overflowX: isMobile ? 'auto' : 'visible', overflowY: 'hidden' }}>
                      <div style={{ width: isMobile && mapPlantSizeSeries.length > 0 ? Math.max(window.innerWidth - 40, mapPlantSizeSeries[0].data?.length * 50) : '100%', minWidth: '100%' }}>
                        <ReactApexChart
                          options={{
                            ...areaplantData.options,
                            chart: {
                              ...areaplantData.options.chart,
                              zoom: { enabled: !isMobile },
                              toolbar: { show: !isMobile }
                            }
                          }}
                          series={mapPlantSizeSeries}
                          height={350}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DialogContentText>
          </DialogContent>
        </Dialog>
      </div>
    </div >
  )
}
PotfolioRepGraph.propTypes = {}
export default PotfolioRepGraph
